import type { Option } from "@/models/form";
import type { School, AcademicProgram } from "@/models/school";
import type { LOSType } from "@/models/opal";
import { createStore } from "vuex";
import type {
  AppData,
  Applicant,
  Occupation,
  Configuration,
  Student,
  EnrollmentTerm,
  ApplicationRatePlan,
  CandidateRefiLoan,
  LenderConfiguration,
} from "@/models/application";
import type { InitialDrawRequest } from "@/models/loanConfiguration";
import { $api } from "@/services/api";
import type { FeedbackRequestOption } from "@/models/feedback";
import loanConfigurationModule from "./loanConfigurationModule";
import candidateRefiLoansModule from "@/store/candidateRefiLoansModule";
import showMyRatesModule from "@/store/showMyRatesModule";
import refiLoanConfigurationModule from "@/store/refiLoanConfigurationModule";
import refiRatesSelfSelectModule from "@/store/refiRatesSelfSelectModule";
import pelRatesSelfSelectModule from "@/store/pelRatesSelfSelectModule";
import applicantPersonalInformationModule from "@/store/applicantPersonalInformationModule";
import applicationDecisionModule from "@/store/applicationDecisionModule";
import documentsModule from "@/store/documentsModule";
import consentsModule from "@/store/consentsModule";
import customTextModule from "@/store/customTextModule";
import store from "@/store";
import useApplication from "@/use/application";
import { ProductIds, ItinAccessTypeIds, CitizenshipStatusTypeIds, ProgramIds } from "@/config/constants";
import type { VuexOidcClientSettings } from "vuex-oidc";
import { vuexOidcCreateStoreModule } from "vuex-oidc";
import { oidcSettings, getEnvironment } from "@/config/oidc";
import { WebStorageStateStore } from "oidc-client";
import type { Banner } from "@/models/banner";

const { getLenderConfigurationByAppId } = useApplication();

const oidcClientSettings = {
  ...getEnvironment(),
  ...oidcSettings,
  userStore: new WebStorageStateStore({
    store: window.sessionStorage,
  }),
} as VuexOidcClientSettings;

const oidcStoreSettings = {
  namespaced: true,
  dispatchEventsOnWindow: true,
};

const oidcEventListeners = {
  userLoaded: (user: any) => console.log("OIDC User Loaded:", user),
  userUnloaded: () => console.log("OIDC User Unloaded"),
  accessTokenExpiring: () => console.log("Access token will expire"),
  accessTokenExpired: () => {
    console.log("Access token expired.");
    // #TODO need to decide what the expired access token behavior looks like
    // const { signOut } = useSignOut();
    // signOut();
  },
  silentRenewError: () => console.log("OIDC User Unloaded"),
  userSignedOut: () => console.log("OIDC User Signed Out"),
  oidcError: (payload: any) => console.log("OIDC Error", payload),
  automaticSilentRenewError: (payload: any) => console.log("OIDC Automatic Silent Renew Error", payload),
};

export default createStore({
  state: {
    configuration: {
      cdnUrl: "https://cusc-lz01-nonprod-cdn.azureedge.net",
    } as Configuration,
    appData: {} as AppData,
    lenderData: undefined as undefined | LenderConfiguration,
    program: {
      pricingTiers: [],
      programDetails: {},
    },
    registrationLookups: {
      phoneTypes: [],
      membershipTypes: [],
    },
    initialDrawRequest: {} as InitialDrawRequest,
    appRatePlans: [] as ApplicationRatePlan[],
    consentTypes: [] as any[],
    membershipTypeOptions: [] as any[],
    phoneTypeOptions: [] as Option[],
    programLimits: [],
    schools: [] as School[],
    leftPanelText: "",
    enrollmentTerms: [] as EnrollmentTerm[],
    enrollmentTermOptions: [] as Option[],
    school: {} as School,
    enrollmentTerm: {} as EnrollmentTerm,
    enrollmentStatuses: [] as Option[],
    academicPrograms: [] as AcademicProgram[],
    academicProgramOptions: [] as Option[],
    coursesOfStudy: [] as any[],
    coursesOfStudyOptions: [] as Option[],
    coursesOfStudyLOCOptions: [] as Option[],
    coursesOfStudyREFIOptions: [] as Option[],
    schoolLevels: [] as any[],
    schoolLevelOptions: [] as Option[],
    feedbackFormOptions: [] as FeedbackRequestOption[],
    addressUnitTypes: [] as any[],
    addressUnitTypeOptions: [] as Option[],
    addressUnitTypePrefixes: [] as string[],
    citizenshipTypes: [] as LOSType[],
    citizenshipTypeOptions: [] as Option[],
    states: [] as LOSType[],
    stateOptions: [] as Option[],
    nationalIdNumberTypes: [] as LOSType[],
    personalInfoFormLookups: {} as any,
    itinTooltipText:
      "If you have been issued an ITIN (Individual Taxpayer Identification Number) in lieu of a Social Security Number, enter the 9-digit ITIN into the SSN fields. To verify your identity, a representative will contact you and request a copy of your ITIN letter and/or current passport. We may also contact you for documentation of how long you have been living in the United States.",
    showItinIndicator: false,
    updateEmailModalActive: false,
    refiStyling: true,
    softPullEnabled: false,
    loanConfigurationInfo: {} as any,
    schoolInfoLookupsLoaded: false,
    surveyQuestionTypes: [] as LOSType[],
    housingTypeOptions: [] as Option[],
    occupations: [] as Occupation[],
    occupationOptions: [] as Option[],
    degreeLevels: [] as Option[],
    refiProgramTypes: [],
    programTypesByDegreeLevel: [],
    privacyBanner: { showBannerFirstPage: true, showBannerApplicantPage: true } as Banner,
    selectedDegreeLevelTypeId: undefined as undefined | number,
    selectedProgramTypeId: undefined as undefined | number,
    selectedPricingTier: {} as any,
    programTypeIdMissing: false as boolean,
    documentTypes: [] as any[],
    retrievingSchools: false as boolean,
  },
  mutations: {
    SET_FLOW_TYPE(state: any, flow: number) {
      state.appData.flowType = flow;
    },
    SET_RETRIEVING_SCHOOLS(state: any, flag: boolean) {
      state.retrievingSchools = flag;
    },
    SET_SURVEY_QUESTION_TYPES(state: any, surveyQuestionTypes: any) {
      state.surveyQuestionTypes = surveyQuestionTypes;
    },
    SET_HOUSING_TYPE_OPTIONS(state: any, housingTypeOptions: any) {
      state.housingTypeOptions = housingTypeOptions;
    },
    SET_INITIAL_DRAW_REQUEST(state: any, initialDrawRequest: InitialDrawRequest) {
      state.initialDrawRequest = initialDrawRequest;
    },
    SET_APP_RATE_PLANS(state: any, appRatePlans: any) {
      state.appRatePlans = appRatePlans;

      state.appData.application.ratePlans = appRatePlans ?? [];
    },
    SET_CONSENT_TYPES(state: any, consentTypes: any) {
      state.consentTypes = consentTypes;
    },
    SET_LOAN_CONFIGURATION_INFO(state: any, loanConfigInfo: any) {
      state.loanConfigurationInfo = loanConfigInfo;
    },
    SET_APPLICATION_METADATA(state: any, metadata: any) {
      state.appData.application.metadata = metadata;
    },
    ADD_APPLICANT(state: any, applicant: Applicant) {
      state.appData.applicants.push(applicant);
    },
    SET_APPLICANT(state: any, applicant: Applicant) {
      const index = state.appData.applicants.findIndex((a: Applicant) => a.id === applicant.id);

      if (index >= 0) {
        let updatedApplicant = {
          ...state.appData.applicants[index],
        };

        updatedApplicant.applicantTypeId = applicant?.applicantTypeId;
        updatedApplicant.firstName = applicant?.firstName;
        updatedApplicant.middleName = applicant?.middleName;
        updatedApplicant.lastName = applicant?.lastName;
        updatedApplicant.nationalIdNumber = applicant?.nationalIdNumber;
        updatedApplicant.nationalIdNumberTypeId = applicant?.nationalIdNumberTypeId;
        updatedApplicant.citizenshipStatusId = applicant?.citizenship || applicant?.citizenshipStatusId;
        updatedApplicant.dateOfBirth = applicant?.dateOfBirth;
        updatedApplicant.contactInfo = applicant?.contactInfo;
        updatedApplicant.concierge = applicant?.concierge;

        if (applicant?.contactInfo) {
          if (!updatedApplicant.contactInfo) {
            updatedApplicant.contactInfo = {
              applicantId: applicant?.id,
            };
          }
          updatedApplicant.contactInfo.applicantId = applicant?.contactInfo?.applicantId;
          updatedApplicant.contactInfo.emailAddress = applicant?.contactInfo?.emailAddress;
          updatedApplicant.contactInfo.phoneNumber = applicant?.contactInfo?.phoneNumber;
          updatedApplicant.contactInfo.phoneNumberTypeId = applicant?.contactInfo?.phoneNumberTypeId;
          updatedApplicant.contactInfo.preferredContactTypeId = applicant?.contactInfo?.preferredContactTypeId;
        }

        updatedApplicant.userId = applicant?.userId;

        if (applicant?.membership) {
          updatedApplicant.membership = {
            applicantId: applicant?.id,
          };

          updatedApplicant.membership.isMember = applicant?.membership?.isMember;
          updatedApplicant.membership.membershipStatusId = applicant?.membership?.membershipStatusId;
          updatedApplicant.membership.memberId = applicant?.membership?.memberId;
        }

        // Spouse step
        if (applicant?.spouse) {
          if (!applicant.spouse) {
            updatedApplicant.spouse = {};
          }
          updatedApplicant.spouse = applicant?.spouse;
        }

        state.appData.applicants[index] = Object.assign({}, updatedApplicant);
      }
    },
    SET_STUDENT(state: any, student: Student) {
      if (!state.appData.application?.student) {
        state.appData.application.student = {};
      }

      state.appData.application.student.applicantId = student.applicantId;
      state.appData.application.student.applicationId = student.applicationId;
      state.appData.application.student.firstName = student.firstName;
      state.appData.application.student.lastName = student.lastName;
      state.appData.application.student.citizenshipStatusId = student.citizenshipStatusId;
      state.appData.application.student.dateOfBirth = student.dateOfBirth;
      state.appData.application.student.nationalIdNumber = student.nationalIdNumber;
      state.appData.application.student.nationalIdNumberTypeId = student.nationalIdNumberTypeId;
      state.appData.application.student.addresses = student.addresses;
      // state.appData.application.student = student;
    },
    SET_ADDRESS_UNIT_TYPES(state: any, types: LOSType[]) {
      state.addressUnitTypes = types;
    },
    SET_ADDRESS_UNIT_TYPE_OPTIONS(state: any, options: Option[]) {
      state.addressUnitTypeOptions = options;
    },
    SET_ADDRESS_UNIT_TYPE_PREFIXES(state: any, prefixes: string[]) {
      state.addressUnitTypePrefixes = prefixes;
    },
    SET_CITIZENSHIP_TYPES(state: any, types: LOSType[]) {
      state.citizenshipTypes = types;
    },
    SET_CITIZENSHIP_TYPE_OPTIONS(state: any, options: Option[]) {
      state.citizenshipTypeOptions = options;
    },
    SET_SHOW_ITIN_INDICATOR(state: any, flag: boolean) {
      state.showItinIndicator = flag;
    },
    SET_NATIONAL_ID_NUMBER_TYPES(state: any, types: LOSType[]) {
      state.nationalIdNumberTypes = types;
    },
    SET_STATES(state: any, states: LOSType[]) {
      state.states = states;
    },
    SET_STATE_OPTIONS(state: any, options: Option[]) {
      state.stateOptions = options;
    },
    SET_PERSONAL_INFORMATION_FORM_LOOKUPS(state: any, lookups: any) {
      state.personalInfoFormLookups = lookups;
    },
    SET_APP_DATA(state: any, appData: AppData) {
      window.localStorage.setItem("appInProgress", JSON.stringify(appData));
      window.localStorage.setItem("product", (appData?.application?.productTypeId || 0)?.toString());
      window.localStorage.setItem("program", (appData?.application?.programTypeId || 0).toString());
      window.localStorage.setItem("leadSourceId", (appData?.application?.leadSourceId || 0)?.toString());
      window.localStorage.setItem("slc-lid", (appData?.application?.lenderId || 0).toString());

      state.appData = appData;
    },
    SET_APPLICANTS(state: any, applicants: Array<Applicant>) {
      state.appData.applicants = applicants;
    },
    SET_SCHOOLS(state: any, schools: School[]) {
      state.schools = schools;
    },
    SET_LENDER_DATA(state: any, lenderData: any) {
      // Determine if page should follow light or dark theme
      if (lenderData?.primaryColor) {
        const parsedHex = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(lenderData.primaryColor);
        const rgb = parsedHex
          ? {
              r: parseInt(parsedHex[1], 16),
              g: parseInt(parsedHex[2], 16),
              b: parseInt(parsedHex[3], 16),
            }
          : null;

        if (rgb !== null) {
          // determine if light or dark theme is needed
          const luminance = Math.round((rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000);

          // Adding else statement just in case client changes. Mainly for testing...
          if (luminance > 118) {
            document.body.classList.add("cuscd-dark");
          } else {
            document.body.classList.remove("cuscd-dark");
          }
        }
      }

      state.lenderData = lenderData;
      window.localStorage.setItem("lenderData", JSON.stringify(lenderData));
    },
    SET_LEFT_PANEL_TEXT(state: any, leftPanelText: string) {
      state.leftPanelText = leftPanelText;
    },
    SET_ENROLLMENT_TERMS(state: any, enrollmentTerms: any[]) {
      state.enrollmentTerms = enrollmentTerms;
    },
    SET_ENROLLMENT_TERM_OPTIONS(state: any, options: Option[]) {
      state.enrollmentTermOptions = options;
    },
    SET_ENROLLMENT_STATUSES(state: any, enrollmentStatuses: any[]) {
      state.enrollmentStatuses = enrollmentStatuses;
    },
    SET_SCHOOL_CONFIG(state: any, schoolInfo: any) {
      state.school = schoolInfo.school;
      state.appData.application.schoolConfiguration.schoolUndecided = schoolInfo.schoolUndecided;
      state.appData.application.schoolConfiguration.schoolName = schoolInfo.school.schoolName;
      state.appData.application.schoolConfiguration.schoolId = schoolInfo.school.id;
      state.appData.application.schoolConfiguration.graduationDate = schoolInfo.graduationDate;
      state.appData.application.schoolConfiguration.academicTerm = schoolInfo?.academicTerm;

      if (schoolInfo.enrollmentTermId) {
        state.appData.application.schoolConfiguration.academicTermId = schoolInfo.enrollmentTermId;
      }

      if (schoolInfo.enrollmentTermDescription) {
        state.appData.application.schoolConfiguration.academicTerm = schoolInfo.enrollmentTermDescription;
      }

      if (schoolInfo?.enrollmentStatusId) {
        const enrollmentStatusId =
          typeof schoolInfo?.enrollmentStatusId === "string"
            ? parseInt(schoolInfo?.enrollmentStatusId)
            : schoolInfo?.enrollmentStatusId;
        state.appData.application.schoolConfiguration.enrollmentStatus = enrollmentStatusId;
      }

      if (schoolInfo?.academicProgramId) {
        state.appData.application.schoolConfiguration.academicProgramId = Number(schoolInfo?.academicProgramId);
      }

      if (schoolInfo?.academicProgramName) {
        state.appData.application.schoolConfiguration.academicProgramName = schoolInfo?.academicProgramName;
      }
    },
    SET_ENROLLMENT_TERM(state: any, enrollmentTerm: EnrollmentTerm) {
      state.enrollmentTerm = enrollmentTerm;
    },
    SET_PROGRAM(state: any, program: any) {
      state.program = program;
    },
    SET_PROGRAM_LIMITS(state: any, programLimits: any) {
      state.programLimits = programLimits;
    },
    SET_ANNUAL_INCOME_LIMIT(state: any, annualIncomeLimit: any) {
      state.annualIncomeLimit = annualIncomeLimit;
    },
    SET_ACADEMIC_PROGRAMS(state: any, academicPrograms: AcademicProgram[]) {
      state.academicPrograms = academicPrograms;
    },
    SET_ACADEMIC_PROGRAM_OPTIONS(state: any, options: Option[]) {
      state.academicProgramOptions = options;
    },
    SET_COURSES_OF_STUDY(state: any, coursesOfStudy: any) {
      state.coursesOfStudy = coursesOfStudy;
    },
    SET_COURSES_OF_STUDY_OPTIONS(state: any, coursesOfStudyOptions: any) {
      state.coursesOfStudyOptions = coursesOfStudyOptions;
    },
    SET_COURSES_OF_STUDY_OPTIONS_LOC(state: any, coursesOfStudyOptions: any) {
      state.coursesOfStudyLOCOptions = coursesOfStudyOptions;
    },
    SET_COURSES_OF_STUDY_OPTIONS_REFI(state: any, coursesOfStudyOptions: any) {
      state.coursesOfStudyREFIOptions = coursesOfStudyOptions;
    },
    SET_SCHOOL_LEVELS(state: any, schoolLevels: any) {
      state.schoolLevels = schoolLevels;
    },
    SET_SCHOOL_LEVEL_OPTIONS(state: any, schoolLevelOptions: any) {
      state.schoolLevelOptions = schoolLevelOptions;
    },
    SET_DEGREE_LEVELS(state: any, degreeLevels: any) {
      state.degreeLevels = degreeLevels;
    },
    SET_SELECTED_DEGREE_LEVEL_TYPE_ID(state: any, degreeLevelTypeId: number) {
      state.selectedDegreeLevelTypeId = degreeLevelTypeId;
    },
    SET_SELECTED_PROGRAM_TYPE_ID(state: any, programTypeId: number) {
      state.selectedProgramTypeId = programTypeId;
    },
    SET_PROGRAM_TYPES_BY_DEGREE_LEVEL(state: any, programs: any) {
      // reset the field
      state.programTypesByDegreeLevel = [];

      state.programTypesByDegreeLevel = programs;
    },
    SET_FEEDBACK_FORM_OPTIONS(state: any, options: any) {
      state.feedbackFormOptions = options;
    },
    OPEN_UPDATE_EMAIL_MODAL(state: any) {
      state.updateEmailModalActive = true;
    },
    CLOSE_UPDATE_EMAIL_MODAL(state: any) {
      state.updateEmailModalActive = false;
    },
    SET_REGISTRATION_LOOKUPS(state: any, lookups: any) {
      state.registrationLookups = lookups;
    },
    SET_MEMBERSHIP_TYPE_OPTIONS(state: any, options: any) {
      state.membershipTypeOptions = options;
    },
    SET_PHONE_TYPE_OPTIONS(state: any, options: any) {
      state.phoneTypeOptions = options;
    },
    SET_LOAN_CONFIGURATION(state: any, loanConfiguration: any) {
      state.appData.application.loanConfiguration = loanConfiguration;
    },
    DG_SUBMIT_SCHOOL_INFO(state: any, schoolInfo: any) {
      if (!state.appData.application.schoolConfiguration) {
        state.appData.application.schoolConfiguration = {
          fullTime: undefined,
          schoolId: undefined,
          schoolName: undefined,
          schoolLevelTypeId: undefined,
          academicTerm: undefined,
          academicTermId: undefined,
          academicProgramId: undefined,
          academicProgramName: undefined,
        };
      }
      state.appData.application.schoolConfiguration.enrollmentStatus = schoolInfo?.enrollmentStatus;
      state.appData.application.schoolConfiguration.fullTime = schoolInfo?.enrollment;
      state.appData.application.schoolConfiguration.schoolId = schoolInfo?.school?.id;
      state.appData.application.schoolConfiguration.schoolName = schoolInfo?.school?.schoolName;
      state.appData.application.schoolConfiguration.schoolLevelTypeId =
        typeof schoolInfo.gradeLevel === "string" ? parseInt(schoolInfo.gradeLevel) : schoolInfo.gradeLevel;

      if (schoolInfo.academicProgramId) {
        state.appData.application.schoolConfiguration.academicProgramId = schoolInfo.academicProgramId;
      }

      if (schoolInfo.academicProgramName) {
        state.appData.application.schoolConfiguration.academicProgramName = schoolInfo.academicProgramName;
      }

      if (schoolInfo.enrollmentTermDescription) {
        state.appData.application.schoolConfiguration.academicTerm = schoolInfo.enrollmentTermDescription;
      }

      if (schoolInfo.enrollmentTermId) {
        state.appData.application.schoolConfiguration.academicTermId = schoolInfo.enrollmentTermId;
      }

      if (state.selectedProgramTypeId) {
        state.appData.application.programTypeId = state.selectedProgramTypeId;
      }
    },
    SUBMIT_SCHOOL_INFO(state: any, schoolInfo: any) {
      if (!state.appData.application.schoolConfiguration) {
        state.appData.application.schoolConfiguration = {
          schoolUndecided: undefined,
          fullTime: undefined,
          schoolId: undefined,
          schoolName: undefined,
          schoolLevelTypeId: undefined,
          academicTerm: undefined,
          academicTermId: undefined,
          academicProgramId: undefined,
          academicProgramName: undefined,
        };
      }
      state.appData.application.schoolConfiguration.enrollmentStatus = schoolInfo?.enrollmentStatus;
      state.appData.application.schoolConfiguration.schoolUndecided = schoolInfo?.schoolUndecided;
      state.appData.application.schoolConfiguration.fullTime = schoolInfo?.enrollment;
      state.appData.application.schoolConfiguration.schoolId = schoolInfo?.school?.id;
      state.appData.application.schoolConfiguration.schoolName = schoolInfo?.school?.schoolName;
      state.appData.application.schoolConfiguration.schoolLevelTypeId =
        typeof schoolInfo.gradeLevel === "string" ? parseInt(schoolInfo.gradeLevel) : schoolInfo.gradeLevel;

      if (schoolInfo.enrollmentTermDescription) {
        state.appData.application.schoolConfiguration.academicTerm = schoolInfo.enrollmentTermDescription;
      }

      if (schoolInfo.enrollmentTermId) {
        state.appData.application.schoolConfiguration.academicTermId = schoolInfo.enrollmentTermId;
      }

      if (schoolInfo.academicProgramId) {
        state.appData.application.schoolConfiguration.academicProgramId = schoolInfo.academicProgramId;
      }

      if (schoolInfo.academicProgramName) {
        state.appData.application.schoolConfiguration.academicProgramName = schoolInfo.academicProgramName;
      }
    },
    REFI_SUBMIT_SCHOOL_INFO(state: any, schoolInfo: any) {
      if (!state.appData.application?.applicationRefiSchool) {
        state.appData.application.applicationRefiSchool = {};
      }

      state.appData.application.programTypeId = state.selectedProgramTypeId;
      state.appData.application.applicationRefiSchool.schoolId = schoolInfo.school.id;
      state.appData.application.applicationRefiSchool.schoolName = schoolInfo.school.schoolName;
      state.appData.application.applicationRefiSchool.degreeLevelTypeId = schoolInfo.degreeLevelTypeId;

      if (schoolInfo.academicProgramId) {
        state.appData.application.applicationRefiSchool.academicProgramId = schoolInfo.academicProgramId;
      }

      if (schoolInfo.academicProgramName) {
        state.appData.application.applicationRefiSchool.academicProgramName = schoolInfo.academicProgramName;
      }
    },
    SET_SELECTED_PRICING_TIER(state: any, pricingTier: any) {
      state.selectedPricingTier = pricingTier;
    },
    SET_PROGRAM_TYPE_ID_MISSING_FLAG(state: any, flag: boolean) {
      state.programTypeIdMissing = flag;
    },
    SET_DOCUMENT_TYPES(state: any, types: any) {
      state.documentTypes = types;
    },
    SET_APP_DATA_CANDIDATE_REFI_LOANS(state: any, candidateRefiLoans: CandidateRefiLoan[]) {
      state.appData.application.candidateRefiLoans = candidateRefiLoans;
    },
    SET_OCCUPATIONS(state: any, occupations: Occupation[]) {
      state.occupations = occupations;
    },
    SET_OCCUPATION_OPTIONS(state: any, options: Option[]) {
      state.occupationOptions = options;
    },
    SET_APPLICANT_USER_ID(state: any, payload: { userId: string; applicantId: number }) {
      const applicant = state.appData.applicants.find((a: Applicant) => a.id === payload.applicantId);
      applicant.userId = payload.userId;
    },
  },
  actions: {
    setFlowType({ commit }, payload) {
      commit("SET_FLOW_TYPE", payload);
    },
    setApplicantUserId({ commit }, payload) {
      commit("SET_APPLICANT_USER_ID", payload);
    },
    setRetrievingSchools({ commit }, payload) {
      commit("SET_RETRIEVING_SCHOOLS", payload);
    },
    setAppRatePlans({ commit }, payload) {
      commit("SET_APP_RATE_PLANS", payload);
    },
    setInitialDrawRequest({ commit }, payload) {
      commit("SET_INITIAL_DRAW_REQUEST", payload);
    },
    setSelectedDegreeLevelTypeId({ commit }, payload) {
      commit("SET_SELECTED_DEGREE_LEVEL_TYPE_ID", payload);
    },
    setSelectedProgramTypeId({ commit }, payload) {
      commit("SET_SELECTED_PROGRAM_TYPE_ID", payload);
    },
    setProgramTypesByDegreeLevel({ commit }, payload) {
      commit("SET_PROGRAM_TYPES_BY_DEGREE_LEVEL", payload);
    },
    submitSchoolInfo({ commit }, payload) {
      commit("SUBMIT_SCHOOL_INFO", payload);
    },
    dgSubmitSchoolInfo({ commit }, payload) {
      commit("DG_SUBMIT_SCHOOL_INFO", payload);
    },
    async refiSubmitSchoolInfo({ state, commit, dispatch }, payload) {
      commit("REFI_SUBMIT_SCHOOL_INFO", payload);

      const lenderId = state?.appData?.application?.lenderId;
      const productTypeId = state?.appData?.application?.productTypeId;
      const programTypeId = state?.appData?.application?.programTypeId;

      const lenderConfig = await getLenderConfigurationByAppId(
        state?.appData?.application?.id,
        lenderId,
        productTypeId,
        programTypeId,
      );

      await dispatch("updateLenderData", lenderConfig);
    },
    setLoanConfigurationInfo({ commit }, payload) {
      commit("SET_LOAN_CONFIGURATION_INFO", payload);
    },
    addApplicant({ commit }, paylod) {
      commit("ADD_APPLICANT", paylod);
    },
    openUpdateEmailModal({ commit }) {
      commit("OPEN_UPDATE_EMAIL_MODAL");
    },
    closeUpdateEmailModal({ commit }) {
      commit("CLOSE_UPDATE_EMAIL_MODAL");
    },
    setApplicants({ commit }, payload) {
      commit("SET_APPLICANTS", payload);
    },
    setAppData({ commit }, payload) {
      commit("SET_APP_DATA", payload);
    },
    setLenderData({ commit }, payload) {
      commit("SET_LENDER_DATA", payload);
    },
    setDefaultLender({ commit }) {
      commit("SET_LENDER_DATA", {
        primaryColor: "#f7941e",
        secondaryColor: "#096fb1",
        program: {
          annualIncomeLimit: 36000,
        },
      });
    },
    setLeftPanelText({ commit }, payload) {
      commit("SET_LEFT_PANEL_TEXT", payload);
    },
    setAddressUnitTypes({ commit }, payload) {
      commit("SET_ADDRESS_UNIT_TYPES", payload);
    },
    setAddressUnitTypeOptions({ commit }, payload) {
      commit("SET_ADDRESS_UNIT_TYPE_OPTIONS", payload);
    },
    setAddressUnitTypePrefixes({ commit }, payload) {
      commit("SET_ADDRESS_UNIT_TYPE_PREFIXES", payload);
    },
    setCitizenshipTypes({ commit }, payload) {
      commit("SET_CITIZENSHIP_TYPES", payload);
    },
    setCitizenshipTypeOptions({ commit }, payload) {
      commit("SET_CITIZENSHIP_TYPE_OPTIONS", payload);
    },
    setNationalIdNumberTypes({ commit }, payload) {
      commit("SET_NATIONAL_ID_NUMBER_TYPES", payload);
    },
    setPersonalInfoFormLookups({ commit }, payload) {
      commit("SET_PERSONAL_INFORMATION_FORM_LOOKUPS", payload);
    },
    setStates({ commit }, payload) {
      commit("SET_STATES", payload);
    },
    setStateOptions({ commit }, payload) {
      commit("SET_STATE_OPTIONS", payload);
    },
    setSchools({ commit }, payload) {
      commit("SET_SCHOOLS", payload);
    },
    setEnrollmentTerms({ commit }, payload) {
      commit("SET_ENROLLMENT_TERMS", payload);
    },
    setEnrollmentTermOptions({ commit }, payload) {
      commit("SET_ENROLLMENT_TERM_OPTIONS", payload);
    },
    setSchoolConfig({ commit }, payload) {
      commit("SET_SCHOOL_CONFIG", payload);
    },
    setEnrollmentTerm({ commit }, payload) {
      commit("SET_ENROLLMENT_TERM", payload);
    },
    setProgram({ commit }, payload) {
      commit("SET_PROGRAM", payload);
    },
    setProgramLimits({ commit }, payload) {
      commit("SET_PROGRAM_LIMITS", payload);
    },
    setCoursesOfStudy({ commit }, payload) {
      commit("SET_COURSES_OF_STUDY", payload);
    },
    setSchoolLevels({ commit }, payload) {
      commit("SET_SCHOOL_LEVELS", payload);
    },
    setShowItinIndicator({ commit }, payload) {
      commit("SET_SHOW_ITIN_INDICATOR", payload);
    },
    setApplicationMetadata({ commit }, payload) {
      commit("SET_APPLICATION_METADATA", payload);
    },
    setLoanConfiguration({ commit }, payload) {
      commit("SET_LOAN_CONFIGURATION", payload);
    },
    setHousingTypeOptions({ commit }, payload) {
      commit("SET_HOUSING_TYPE_OPTIONS", payload);
    },
    setCandidateRefiLoans({ commit }, payload) {
      commit("SET_APP_DATA_CANDIDATE_REFI_LOANS", payload);
    },
    setStudent({ commit }, payload) {
      commit("SET_STUDENT", payload);
    },
    setApplicant({ commit }, payload) {
      commit("SET_APPLICANT", payload);
    },
    findAndReplaceApplicant({ commit }, payload) {
      const applicants = store.state.appData.applicants;

      const index = applicants?.findIndex((applicant: Applicant) => applicant.id === payload.applicantId);

      if (index !== -1) {
        applicants[index] = payload.updatedApplicant;
      }

      commit("SET_APPLICANTS", applicants);
    },
    async getProgram({ state, commit, dispatch }) {
      if (!state.program || state.program?.pricingTiers?.length === 0 || state.programLimits?.length === 0) {
        if (state?.appData?.application?.programTypeId) {
          commit("SET_PROGRAM_TYPE_ID_MISSING_FLAG", false);

          const rates = await $api.configuration.getRates(
            state?.appData?.application?.lenderId,
            state?.appData?.application?.programTypeId,
            state?.appData?.application?.productTypeId,
          );
          if (rates.pricingTiers && rates.programDetails) {
            dispatch("setProgram", rates);
            dispatch("setProgramLimits", rates.programDetails?.programLimits);
          }
        } else {
          commit("SET_PROGRAM_TYPE_ID_MISSING_FLAG", true);
        }
      }
    },
    async getFeedbackFormOptions({ state, commit }) {
      try {
        if (state.feedbackFormOptions?.length <= 0) {
          const options = await $api.feedback.getFeedbackFormOptions(2);
          commit("SET_FEEDBACK_FORM_OPTIONS", options);
        }
      } catch (error) {
        console.log(error);
      }
    },
    async getEnrollmentTerms({ state, commit }, payload) {
      try {
        const { lenderId, productTypeId, programTypeId } = state.appData.application;

        const schoolId =
          payload ||
          state?.appData?.application?.schoolConfiguration?.schoolId ||
          state?.appData?.application?.schoolId;

        const lookups = await $api.school.getEnrollmentTerms(
          schoolId ?? Number(0),
          lenderId,
          productTypeId,
          programTypeId || state.selectedProgramTypeId,
        );

        if (lookups.length > 0) {
          commit("SET_ENROLLMENT_TERMS", lookups);

          const enrollmentTermOptions = lookups.map((o: any) => {
            return {
              label: o.description,
              value: o.id,
            };
          });

          commit("SET_ENROLLMENT_TERM_OPTIONS", enrollmentTermOptions);
        }
      } catch (error) {
        console.log("Error: getEnrollmentTerms()", error);
      }
    },
    async getRegistrationLookups({ state, commit }) {
      try {
        if (
          state.registrationLookups?.phoneTypes?.length <= 0 ||
          state.registrationLookups?.membershipTypes?.length <= 0
        ) {
          const lookups = await $api.lookups.getRegistrationLookups();
          if (lookups.phoneTypes && lookups.membershipTypes) {
            commit("SET_REGISTRATION_LOOKUPS", lookups);
          }
          if (lookups.membershipTypes.length > 0) {
            const membershipTypeOptions = lookups.membershipTypes;
            commit("SET_MEMBERSHIP_TYPE_OPTIONS", membershipTypeOptions);
          }

          if (lookups.phoneTypes.length > 0) {
            const phoneTypeOptions = lookups.phoneTypes.map((type: any) => {
              return {
                label: type.name,
                value: type.id,
              };
            });

            commit("SET_PHONE_TYPE_OPTIONS", phoneTypeOptions);
          }
        }
      } catch (error) {
        console.log("Error: getRegistrationLookups()", error);
      }
    },
    async getAcademicPrograms(
      { state, commit },
      payload: { schoolId: number; degreeLevelTypeId?: number | null; programTypeId?: number | null },
    ) {
      const lenderId = state.appData.application.lenderId;
      const productTypeId = state.appData.application.productTypeId;
      const programTypeId = payload?.programTypeId ?? state.appData.application.programTypeId;
      const degreeLevelTypeId = productTypeId !== ProductIds.LOC ? payload.degreeLevelTypeId : null;
      const flowTypeId = state.appData?.flowType || state.appData?.flowTypeId;

      const academicPrograms = await $api.school.getAcademicPrograms(
        payload?.schoolId,
        lenderId,
        productTypeId,
        programTypeId,
        degreeLevelTypeId,
        flowTypeId,
      );

      if (academicPrograms.errors) {
        commit("SET_ACADEMIC_PROGRAMS", []);
        commit("SET_ACADEMIC_PROGRAM_OPTIONS", []);

        return;
      }

      const academicProgramOptions = (academicPrograms || []).map((o: AcademicProgram) => {
        return {
          label: o.description,
          value: o.id,
        };
      });

      commit("SET_ACADEMIC_PROGRAMS", academicPrograms);
      commit("SET_ACADEMIC_PROGRAM_OPTIONS", academicProgramOptions);
    },
    async getSchoolInfoLookupsRefi({ state, commit }, programTypeId: number) {
      commit("SET_RETRIEVING_SCHOOLS", true);

      const lookups = await $api.lookups.getRefiSchoolInfoLookups(programTypeId);
      if (lookups.coursesOfStudy?.length > 0) {
        commit("SET_COURSES_OF_STUDY", lookups.coursesOfStudy);
        const coursesOfStudy = (lookups.coursesOfStudy || []).map((o: any) => {
          return {
            label: o.description,
            value: o.id,
          };
        });

        const coursesOfStudyREFI = (lookups.coursesOfStudy || []).map((o: any) => {
          return {
            label: o.description,
            value: o.id,
          };
        });

        commit("SET_COURSES_OF_STUDY_OPTIONS", coursesOfStudy);
        commit("SET_COURSES_OF_STUDY_OPTIONS_REFI", coursesOfStudyREFI);
      }

      const res = await $api.school.getSchoolsByProgram(
        programTypeId,
        state.appData?.application?.productTypeId,
        state?.appData?.application?.lenderId,
      );

      if (res?.schools?.length > 0) {
        commit("SET_SCHOOLS", res.schools);
      }

      commit("SET_RETRIEVING_SCHOOLS", false);
    },
    resetSchools({ commit }) {
      commit("SET_SCHOOLS", []);
    },
    async getSchoolInfoLookups(
      { state, commit },
      payload?: { programTypeId?: number; refresh?: boolean; academicId?: number | null },
    ) {
      try {
        const program =
          payload?.programTypeId || state.selectedProgramTypeId || state?.appData?.application?.programTypeId;

        if (state.schoolLevels?.length <= 0 || state.enrollmentStatuses?.length <= 0 || payload?.refresh) {
          const lookups = await $api.lookups.getSchoolInfoLookups(program);

          if (lookups.schoolLevels?.length > 0) {
            commit("SET_SCHOOL_LEVELS", lookups.schoolLevels);

            const schoolLevels = (lookups.schoolLevels || []).map((o: any) => {
              return {
                label: o.name,
                value: o.id || "",
              };
            });

            commit("SET_SCHOOL_LEVEL_OPTIONS", schoolLevels);
          }

          if (lookups.enrollmentStatuses?.length > 0) {
            commit("SET_ENROLLMENT_STATUSES", lookups.enrollmentStatuses);
          }
        }

        if ((state.schools?.length <= 0 && program) || payload?.refresh) {
          const res = await $api.school.getSchoolsByProgram(
            program,
            state.appData?.application?.productTypeId,
            state?.appData?.application?.lenderId,
            payload?.academicId,
          );
          if (res?.schools?.length > 0) {
            commit("SET_SCHOOLS", res.schools);
          } else {
            commit("SET_SCHOOLS", []);
          }
        }
      } catch (error) {
        console.log("Error: getSchoolInfoLookups()", error);
      }
    },
    async getAdditionalInfoLookups({ commit, state }, request: { applicantTypeId: number; applicantId: number }) {
      try {
        const lookups = await $api.lookups.getAdditionalInfoLookups(
          request.applicantTypeId,
          request.applicantId,
          state?.appData?.application?.productTypeId,
          state?.appData?.application?.programTypeId,
          state?.appData?.application?.id,
        );
        if (lookups.error) {
          return { error: true };
        } else {
          if (lookups.housingTypes && lookups?.housingTypes?.length > 0) {
            const options = lookups.housingTypes.map((o: any) => {
              return {
                label: o.description || o.label,
                value: o.id || o.value,
              };
            });

            commit("SET_HOUSING_TYPE_OPTIONS", options);
          }

          if (lookups.occupations) {
            commit("SET_OCCUPATIONS", lookups.occupations);
            commit("SET_OCCUPATION_OPTIONS", lookups.occupationOptions);
          }
        }
      } catch (error) {
        console.log("Error: getAdditionalInfoLookups()", error);
      }
    },
    async getConsentTypes({ state, commit }) {
      try {
        if (state.consentTypes?.length <= 0) {
          const lookups = await $api.lookups.getConsentTypes();
          if (lookups?.length > 0) {
            commit("SET_CONSENT_TYPES", lookups);
          }
        }
      } catch (error) {
        console.log("Error: getConsentTypes()", error);
      }
    },
    async getSurveyQuestionTypes({ state, commit }) {
      try {
        if (state.surveyQuestionTypes?.length <= 0) {
          const lookups = await $api.lookups.getSurveyQuestionTypes();
          if (lookups.success) {
            commit("SET_SURVEY_QUESTION_TYPES", lookups.results);
          }
        }
      } catch (error) {
        console.log("Error: getSurveyQuestionTypes()", error);
      }
    },
    async getAppRatePlans({ state, commit }) {
      const appRatePlans = await $api.applications.getApplicationRatePlansByApplicationId(
        state?.appData?.application?.id,
        state?.appData?.application?.lenderId,
      );
      if (appRatePlans && appRatePlans?.length > 0) {
        const sortedRatePlans = appRatePlans.sort((a: any, b: any) => a.rateTypeId - b.rateTypeId);
        commit("SET_APP_RATE_PLANS", sortedRatePlans);
      }
    },
    async getDegreeLevels({ state, commit }) {
      try {
        if (state.degreeLevels?.length <= 0) {
          const response = await $api.refi.getDegreeLevels();

          if (response?.length > 0) {
            commit("SET_DEGREE_LEVELS", response);
          }
        }
      } catch (error) {
        console.log("Error: getDegreeLevels()", error);
      }
    },
    async getProgramsByDegreeLevelTypeId({ state, commit }, degreeLevelTypeId: number) {
      try {
        const response = await $api.refi.getProgramsByDegreeLevelTypeId(
          state.appData?.application?.lenderId,
          state.appData?.application?.productTypeId,
          degreeLevelTypeId,
        );

        if (response?.data && response?.data?.length > 0) {
          commit("SET_PROGRAM_TYPES_BY_DEGREE_LEVEL", response.data);
        } else {
          commit("SET_PROGRAM_TYPES_BY_DEGREE_LEVEL", []);
        }
      } catch (error) {
        console.log("Error: getProgramsByDegreeLevelTypeId()", error);
      }
    },
    async updateLenderData({ dispatch }, lenderConfig: any) {
      if (lenderConfig?.lid || !lenderConfig?.error) {
        window.localStorage.setItem("lenderData", JSON.stringify(lenderConfig));
        await dispatch("setLenderData", lenderConfig);
      } else {
        if (lenderConfig?.redirectUrl) {
          window.location.href = `${lenderConfig?.redirectUrl}`;
        }
      }
    },
    async saveSelectedCreditScoreRange({ dispatch }, payload) {
      await dispatch("getSurveyQuestionTypes");

      const questionId = store.state.surveyQuestionTypes?.find(
        (s: any) => s.name?.trim() === "SelectedCreditRange",
      )?.id;

      const applicant = payload.applicant;
      const selectedVariableRate = payload?.rates?.find((o: any) => o.type === "Variable");
      const selectedFixedRate = payload?.rates?.find((o: any) => o.type === "Fixed");
      const request = {
        min: payload?.selectedRange?.min,
        max: payload?.selectedRange?.max,
        variableRate: selectedVariableRate?.interestRate?.toFixed(3) || "NULL",
        fixedRate: selectedFixedRate?.interestRate?.toFixed(3) || "NULL",
      };

      const jsonRequest = JSON.stringify(request);
      const surveyResponseRequest = {
        applicantId: applicant?.id,
        questionId: questionId,
        response: jsonRequest,
      } as any;

      const response = await $api.person.addSurveyResponse(surveyResponseRequest);
      return response;
    },
    async getDocumentTypes({ commit }, payload) {
      try {
        const response = await $api.lookups.getDocumentTypes(payload);

        if (response?.results && response?.results?.length > 0) {
          commit("SET_DOCUMENT_TYPES", response.results);
        }
      } catch (error) {
        console.log("Error: getDocumentTypes()", error);
      }
    },
  },
  getters: {
    SOFT_PULL_ENABLED_FLAG: (state): boolean => {
      return state.lenderData?.program?.softPullEnabled ? state.lenderData?.program?.softPullEnabled : false;
    },
    membershipTypeOptions: (state): Array<Option> => {
      return state.membershipTypeOptions;
    },
    updateEmailModalActive: (state): boolean => {
      return state.updateEmailModalActive;
    },
    cuLogoUrl: (state): string => {
      return state.lenderData
        ? new URL(
            `/public/assets/${state.lenderData?.lenderSubdomain || "cusc"}/logo.png`,
            state.configuration.cdnUrl,
          ).toString()
        : "";
    },
    supportPhoneNumber: (state): string => {
      return state.lenderData?.tollFreeNumber ? state.lenderData.tollFreeNumber : "1-800-381-7058";
    },
    states: (state): string => {
      return state.states;
    },
    appData: (state: any) => {
      return state.appData;
    },
    enrollmentTerms: (state: any) => {
      return state.enrollmentTerms;
    },
    primaryApplicantTypeId: (state): number => {
      if (state.lenderData?.applicantTypes?.length > 0) {
        return state.lenderData?.applicantTypes?.find((a: any) => a.name === "Primary Applicant").id || 0;
      } else {
        return 0;
      }
    },
    coApplicantTypeId: (state): number => {
      if (state.lenderData?.applicantTypes?.length > 0) {
        return state.lenderData?.applicantTypes?.find((a: any) => a.name === "Co-Applicant").id || 0;
      } else {
        return 0;
      }
    },
    primaryApplicantPersonId: (state: any) => {
      if (state.appData?.applicants && state.appData?.applicants?.length > 0) {
        return (
          state.appData.applicants.find((p: Applicant) => (p.applicantTypeName || "") === "Primary Applicant").id || 0
        );
      } else {
        return 0;
      }
    },
    coApplicantPersonId: (state: any) => {
      if (state.appData?.applicants && state.appData?.applicants?.length > 0) {
        return state.appData.applicants.find((p: Applicant) => (p.applicantTypeName || "") === "Co-Applicant")?.id || 0;
      } else {
        return 0;
      }
    },
    feedbackFormOptions: (state: any) => {
      return state.feedbackFormOptions;
    },
    itinNationalIdNumberTypeId: (state: any) => {
      return state?.nationalIdNumberTypes?.find((t: LOSType) => t.name === "ITIN")?.id;
    },
    ssnNationalIdNumberTypeId: (state: any) => {
      return state?.nationalIdNumberTypes?.find((t: LOSType) => t.name === "SSN")?.id;
    },
    getCitizenshipTypeOptions: (state: any): any => {
      const coborrowerPathCheck = [
        "coborrowerPersonalInfo",
        "coborrowerInfo",
        "coborrowerInvitedApplicantInfo",
        "refiCosignerPersonalInfo",
        "refiCosignerInfo",
        "refiCosignerInvitedApplicantInfo",
      ].includes(state.appData.currentState?.route);

      const borrowerPathCheck = [
        "borrowerPersonalInfo",
        "borrowerInfo",
        "borrowerInvitedApplicantInfo",
        "refiBorrowerPersonalInfo",
        "refiBorrowerInfo",
        "refiBorrowerInvitedApplicantInfo",
      ].includes(state.appData.currentState?.route);

      // If Coborrower Personal Information Page and Coborrower Only Config - use the configured
      // If Borrower Personal Informatin Page and Borrower Only Config - use the configured
      // If BorrowerAndCoborrower - use the configured
      if (
        (state.lenderData?.itinAccessTypeId &&
          state.lenderData?.itinAccessTypeId === ItinAccessTypeIds.CoborrowerOnly &&
          coborrowerPathCheck) ||
        (state.lenderData?.itinAccessTypeId &&
          state.lenderData?.itinAccessTypeId === ItinAccessTypeIds.BorrowerOnly &&
          borrowerPathCheck) ||
        (state.lenderData?.itinAccessTypeId &&
          state.lenderData?.itinAccessTypeId === ItinAccessTypeIds.BorrowerAndCoborrower)
      ) {
        return state.citizenshipTypeOptions;
      } else {
        // If Borrower Personal Info and Coborrower Only Config Do not show the configured NonCitizen Option
        // If Coborrower Personal Info and Borrower  Only Config Do not show the configured NonCitizen Option
        // If ITIN Access Type = NONE Config Do not show the configured NonCitizen Option (if configured)
        return state.citizenshipTypeOptions.filter((o: Option) => o.value !== CitizenshipStatusTypeIds.NonCitizen);
      }
    },
    adverseActionNoticeDocumentTypeId: (state: any): any => {
      return state?.documentTypes?.find((t: LOSType) => t.name === "Adverse Action")?.id;
    },
    coApplicantTypeDisplayName: (state: any): string => {
      switch (state.appData?.application?.productTypeId) {
        case ProductIds.LOC:
          return "Co-borrower";
        case ProductIds.REFI:
        case ProductIds.PEL:
          return "Cosigner";
        default:
          return "Co-Applicant";
      }
    },
    primaryApplicantTypeDisplayName: (state: any): string => {
      switch (state.appData?.application?.productTypeId) {
        case ProductIds.LOC:
        case ProductIds.PEL:
          if (
            state.appData?.application?.productTypeId === ProductIds.LOC &&
            state.appData?.application?.programTypeId === ProgramIds.PARENT
          ) {
            return "Borrower";
          }

          return "Student Borrower";
        case ProductIds.REFI:
          return "Borrower";
        default:
          return "Primary Applicant";
      }
    },
    primaryApplicantTypeName: (state): string => {
      if (state.lenderData?.applicantTypes?.length > 0) {
        return state.lenderData?.applicantTypes?.find((a: any) => a.name === "Primary Applicant")?.name || "";
      } else {
        return "Primary Applicant";
      }
    },
    coApplicantTypeName: (state): string => {
      if (state.lenderData?.applicantTypes?.length > 0) {
        return state.lenderData?.applicantTypes?.find((a: any) => a.name === "Co-Applicant")?.name || "";
      } else {
        return "Co-Applicant";
      }
    },
    PROGRAM_MINIMUM_LOAN_AMOUNT: (state): number => {
      return state.appData?.application?.metadata?.withCoApplicants === true
        ? state?.lenderData?.program?.borrowerMinLoanAmountJoint
        : state?.lenderData?.program?.borrowerMinLoanAmountSolo;
    },
    PROGRAM_MINIMUM_LOAN_AMOUNT_DISPLAY: (state): string => {
      return state.appData?.application?.metadata?.withCoApplicants === true
        ? state?.lenderData?.program?.borrowerMinLoanAmountJointDisplay
        : state?.lenderData?.program?.borrowerMinLoanAmountSoloDisplay;
    },
    PROGRAM_MINIMUM_INITIAL_DRAW_AMOUNT: (state): number => {
      return state?.lenderData?.program?.minimumInitialDrawAmount;
    },
    PROGRAM_MINIMUM_INITIAL_DRAW_AMOUNT_DISPLAY: (state): string => {
      return state?.lenderData?.program?.minimumInitialDrawAmountDisplay;
    },
    productDisplayName: (state: any) => {
      return state?.lenderData?.program?.productName;
    },
  },
  modules: {
    loanConfigurationModule,
    candidateRefiLoansModule,
    showMyRatesModule,
    refiLoanConfigurationModule,
    refiRatesSelfSelectModule,
    applicantPersonalInformationModule,
    applicationDecisionModule,
    documentsModule,
    consentsModule,
    customTextModule,
    pelRatesSelfSelectModule,
    oidcStore: vuexOidcCreateStoreModule(oidcClientSettings, oidcStoreSettings, oidcEventListeners),
  },
});
